import { useEffect, useState } from 'react';
import { ApolloError, useQuery } from '@apollo/client';

import { GetStripeProgramCoverageDocument } from '@sandtech-hub-monorepo/ehub-client';

/**
 * The webhook that mirrors a new subscription usually lands within a second or
 * two of the learner returning, but it is a separate network path and nothing
 * orders the two. Poll, then stop: an unbounded poll is a tab that talks to
 * the server forever on a page nobody is watching.
 */
const POLL_INTERVAL_MS = 2_000;
const POLL_CEILING_MS = 30_000;

interface UseProgramCoverageResult {
  covered: boolean;
  /**
   * True only until the first answer arrives. `covered` defaults to false, so
   * anything that renders a locked state must wait rather than assert one at
   * a learner who has paid.
   */
  loading: boolean;
  /** Polling reached its ceiling with no coverage. Not a payment failure. */
  timedOut: boolean;
  error: ApolloError | undefined;
}

/**
 * Whether one of the caller's live subscriptions currently unlocks a program.
 *
 * Entitlement is a join, not a flag on the purchase: one subscription covers
 * every program its plan lists, so this is the only honest way to ask. Note
 * PAST_DUE still counts as covered — Stripe is mid-dunning and access is not
 * pulled.
 *
 * `poll` is for the moments just after a checkout, where the answer is
 * expected to change on its own. Everywhere else this is a plain read.
 */
export const useProgramCoverage = (
  programId: string | null,
  { poll = false }: { poll?: boolean } = {}
): UseProgramCoverageResult => {
  const [timedOut, setTimedOut] = useState(false);

  const { data, loading, error, startPolling, stopPolling } = useQuery(
    GetStripeProgramCoverageDocument,
    {
      variables: { programId: programId ?? '' },
      skip: !programId,
      // Coverage changes underneath us when a webhook lands, and a stale
      // `false` locks a paying learner out of what they bought. Serve the
      // cached answer for an instant render, then correct it from the network.
      fetchPolicy: 'cache-and-network',
    }
  );

  const covered = data?.stripeProgramCoverage.covered ?? false;

  useEffect(() => {
    if (!poll || !programId || covered || timedOut) return;

    startPolling(POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [poll, programId, covered, timedOut, startPolling, stopPolling]);

  useEffect(() => {
    // Deliberately keyed on `covered` alone, so this is one ceiling measured
    // from arrival rather than a window that restarts on every poll.
    if (!poll || covered) return;

    const timer = setTimeout(() => setTimedOut(true), POLL_CEILING_MS);
    return () => clearTimeout(timer);
  }, [poll, covered]);

  return {
    covered,
    // `cache-and-network` reports loading during the background refresh too,
    // and a caller that already has an answer must not fall back to a spinner.
    loading: !!programId && loading && !data,
    timedOut: timedOut && !covered,
    error,
  };
};
